import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

/** One at-a-glance number (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md v1 §4.1) — a
 * plain `Box` grid rather than MUI `Grid` (no other page here uses it yet;
 * flex-wrap is enough for a handful of KPI tiles). */
function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Box sx={{ minWidth: 160, flex: "1 1 160px" }}>
      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/**
 * The admin landing dashboard (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md v1 §4.1)
 * — replaces the bare `index → /people` redirect with an "is my provider
 * healthy?" glance: active accounts, device activity, open reports, active
 * invites, and recent signups, all from one `GET /admin/overview` round trip.
 * A signal to jump to the fuller page for anything interesting (Reports,
 * Devices, People) rather than duplicating those pages' own detail here.
 */
export function OverviewPage() {
  const { client, token } = useAuth();

  const query = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () => client!.adminOverview(token!),
    enabled: !!client && !!token,
  });
  const overview = query.data;

  return (
    <MainCard
      title="Overview"
      secondary={
        <Button size="small" variant="outlined" color="secondary" onClick={() => query.refetch()}>
          Refresh
        </Button>
      }
    >
      {query.isError ? <Alert severity="error">{(query.error as Error).message}</Alert> : null}
      {query.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : overview ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          <Stat label="Active accounts" value={overview.users.total} />
          <Stat label="Admins" value={overview.users.admins} />
          <Stat
            label={`Devices active (${overview.devices.activeDays}d)`}
            value={overview.devices.activeLastNDays}
          />
          <Stat label="Devices total" value={overview.devices.total} />
          <Stat label="Reports pending" value={overview.reports.pending} />
          <Stat label="Active invites" value={overview.invites.active} />
          <Stat label={`Signups (${overview.recentSignups.days}d)`} value={overview.recentSignups.count} />
        </Box>
      ) : null}
    </MainCard>
  );
}
